/**
 * Iter63 — Página admin de deduplicación de gestoras.
 *
 * Solo accesible por admin y dir_comercial. Muestra propuestas de fusión agrupadas
 * por nivel de confianza (alta/media/baja) y permite Mergear o Rechazar cada grupo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, Loader2, Merge, X, RefreshCw, AlertTriangle, CheckCircle2, Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { req as request } from '../services/api';

const API = {
  generar: (threshold = 85) => request(`/api/admin/dedupe-gestoras/generar?threshold=${threshold}`, { method: 'POST' }),
  listar: () => request('/api/admin/dedupe-gestoras/propuestas'),
  aprobar: (pid) => request(`/api/admin/dedupe-gestoras/propuestas/${pid}/aprobar`, { method: 'POST' }),
  rechazar: (pid) => request(`/api/admin/dedupe-gestoras/propuestas/${pid}/rechazar`, { method: 'POST' }),
};

const bucketize = (c) => {
  if (c >= 0.95) return { label: 'Confianza alta', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if (c >= 0.90) return { label: 'Confianza media', tone: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { label: 'Confianza baja', tone: 'bg-ink-50 text-ink-600 border-ink-200' };
};

const DedupeGestoras = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [propuestas, setPropuestas] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [actuando, setActuando] = useState(null);  // propuesta_id en curso

  const puede = user && (user.rol === 'admin' || user.rol === 'dir_comercial');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await API.listar();
      setPropuestas(data || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (puede) cargar(); }, [puede, cargar]);

  const onGenerar = async () => {
    setGenerating(true);
    try {
      const res = await API.generar(85);
      toast.success(`${res.propuestas_creadas} propuestas generadas (${res.total_entidades_candidatas} gestoras analizadas)`);
      await cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const onMergear = async (p) => {
    if (!window.confirm(`¿Fusionar ${p.entidades.length} gestoras?\nPrincipal: "${p.entidades[0].nombre}" (${p.entidades[0].num_municipios} municipios).\nLas otras se marcarán como INACTIVO y sus municipios se reasignarán a la principal.`)) return;
    setActuando(p.id);
    try {
      const res = await API.aprobar(p.id);
      toast.success(`Fusionadas. ${res.municipios_reasignados} municipios reasignados, ${res.entidades_inactivadas} duplicadas desactivadas.`);
      await cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActuando(null);
    }
  };

  const onRechazar = async (p) => {
    if (!window.confirm(`Marcar "${p.entidades.map((e) => e.nombre).join(' / ')}" como NO duplicadas? No volverán a proponerse.`)) return;
    setActuando(p.id);
    try {
      await API.rechazar(p.id);
      toast.success('Propuesta rechazada. No volverá a aparecer.');
      await cargar();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActuando(null);
    }
  };

  if (!puede) {
    return (
      <div className="p-10 text-center text-ink-500">
        <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
        Esta pantalla solo es accesible para administradores y dirección comercial.
      </div>
    );
  }

  // Agrupar por bucket de confianza
  const byBucket = { alta: [], media: [], baja: [] };
  propuestas.forEach((p) => {
    if (p.confianza >= 0.95) byBucket.alta.push(p);
    else if (p.confianza >= 0.90) byBucket.media.push(p);
    else byBucket.baja.push(p);
  });

  return (
    <div className="max-w-6xl mx-auto py-6 space-y-6" data-testid="page-dedupe-gestoras">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink-900 flex items-center gap-2">
            <Merge className="w-7 h-7 text-brand-500" /> Deduplicar gestoras
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Gestoras descubiertas por el job de enriquecimiento que probablemente son la misma empresa
            con variantes de nombre. Revisa cada grupo y fusiona o descarta.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" onClick={cargar} disabled={loading}
            data-testid="btn-dedupe-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button
            className="bg-brand-500 hover:bg-brand-600 text-white"
            onClick={onGenerar} disabled={generating}
            data-testid="btn-dedupe-generar"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Merge className="w-4 h-4 mr-1.5" />}
            {generating ? 'Analizando…' : 'Generar propuestas'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-ink-400">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" /> Cargando…
        </div>
      ) : propuestas.length === 0 ? (
        <Card className="p-12 text-center border-ink-200" data-testid="dedupe-vacio">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="font-heading font-semibold text-ink-800 mb-1">Sin propuestas pendientes</p>
          <p className="text-sm text-ink-500">
            Pulsa <span className="font-mono bg-ink-100 px-1.5 rounded text-xs">Generar propuestas</span> para
            analizar las gestoras importadas y buscar posibles duplicadas.
          </p>
        </Card>
      ) : (
        <div className="space-y-8" data-testid="dedupe-lista">
          {['alta', 'media', 'baja'].map((key) => byBucket[key].length > 0 && (
            <section key={key} data-testid={`dedupe-bucket-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="font-heading font-semibold text-lg text-ink-800">
                  {bucketize(byBucket[key][0].confianza).label}
                </h2>
                <Badge className="bg-ink-100 text-ink-600 border-ink-200">
                  {byBucket[key].length} {byBucket[key].length === 1 ? 'grupo' : 'grupos'}
                </Badge>
              </div>
              <div className="space-y-3">
                {byBucket[key].map((p, idx) => {
                  const bucket = bucketize(p.confianza);
                  const totalMunici = p.entidades.reduce((s, e) => s + (e.num_municipios || 0), 0);
                  const principal = p.entidades[0];
                  return (
                    <Card
                      key={p.id}
                      className="p-5 border-ink-200 bg-white"
                      data-testid={`dedupe-grupo-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${bucket.tone} border`}>
                            {(p.confianza * 100).toFixed(1)}%
                          </Badge>
                          <Badge className="bg-brand-50 text-brand-700 border border-brand-200">
                            <Users className="w-3 h-3 mr-1 inline" />
                            {totalMunici} municipios
                          </Badge>
                          <Badge className="bg-ink-50 text-ink-600 border border-ink-200">
                            {p.entidades.length} entidades
                          </Badge>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => onRechazar(p)}
                            disabled={actuando === p.id}
                            data-testid={`btn-rechazar-${idx}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                          <Button
                            size="sm"
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            onClick={() => onMergear(p)}
                            disabled={actuando === p.id}
                            data-testid={`btn-mergear-${idx}`}
                          >
                            {actuando === p.id
                              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              : <Merge className="w-4 h-4 mr-1" />}
                            Mergear
                          </Button>
                        </div>
                      </div>
                      <div className="divide-y divide-ink-100 border border-ink-100 rounded-lg overflow-hidden">
                        {p.entidades.map((e, i) => (
                          <div
                            key={e.id}
                            className={`flex items-center gap-3 px-3 py-2 text-sm ${i === 0 ? 'bg-emerald-50/60' : 'bg-white'}`}
                            data-testid={`dedupe-ent-${idx}-${i}`}
                          >
                            {i === 0 && (
                              <span title="Esta será la principal (la de más municipios)" className="text-amber-500">
                                <Crown className="w-4 h-4" />
                              </span>
                            )}
                            <span className="flex-1 font-medium text-ink-900 truncate" title={e.nombre}>
                              {e.nombre}
                            </span>
                            <span className="text-xs text-ink-500 tabular-nums">
                              {e.num_municipios} municipio{e.num_municipios !== 1 ? 's' : ''}
                            </span>
                            {i === 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px]">
                                PRINCIPAL
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-ink-400 mt-2">
                        Al fusionar, los municipios y contratos de las otras se reasignan a <span className="font-semibold">{principal.nombre}</span>. Las demás entidades se marcan como <span className="font-mono">inactivo</span> (no se borran).
                      </p>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default DedupeGestoras;
